"use client"

import type React from "react"
import { useState, useMemo, useEffect, useRef } from "react"
import {
  Edit,
  Printer,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  FileSpreadsheet,
} from "lucide-react"
import type { DynamicUser, DynamicSchema, TableConfig } from "../types/dynamic"
import { SchemaService } from "../services/schema"
import DynamicEditModal from "./DynamicEditModal"
import DynamicPrintModal from "./DynamicPrintModal"

const exportToExcel = async (
  users: DynamicUser[],
  schema: DynamicSchema,
  config: TableConfig,
  getFieldValue: (user: DynamicUser, fieldName: string) => any,
  formatFieldValue: (value: any, fieldName: string) => string
) => {
  const { default: miniXlsx } = await import("../lib/mini-xlsx.js")

  const headers = config.visibleFields
    .map(f => schema[f]?.label ?? f)

  const rows = users.map(user =>
    config.visibleFields.map(fieldName => {
      const value = getFieldValue(user, fieldName)
      return formatFieldValue(value, fieldName)
    })
  )

  const bytes = miniXlsx([{ name: "Usuarios", headers, rows }])
  const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
  const date = new Date().toISOString().split("T")[0]
  const link = Object.assign(document.createElement("a"), {
    href: URL.createObjectURL(blob),
    download: `Usuarios_${date}.xlsx`,
  })
  link.click()
  URL.revokeObjectURL(link.href)
}

interface DynamicTableProps {
  users: DynamicUser[]
  schema: DynamicSchema
  config: TableConfig
  loading: boolean
  error: string | null
  showToast: (type: "success" | "error" | "info", message: string) => void
  searchTerm: string
  cancelCurrentFetch: () => void
  getFieldValue: (user: DynamicUser, fieldName: string) => any
  formatFieldValue: (value: any, fieldName: string) => string
  filterUsers: (searchTerm: string) => DynamicUser[]
  onEditSuccess: (updatedUser: DynamicUser) => void
  loadingStep?: string
  kioskMode?: boolean
  onPrintUser?: (user: DynamicUser) => void
}

const DynamicTable: React.FC<DynamicTableProps> = ({
  schema,
  config,
  loading,
  error,
  showToast,
  searchTerm,
  cancelCurrentFetch,
  getFieldValue,
  formatFieldValue,
  filterUsers,
  onEditSuccess,
  loadingStep = "Cargando datos dinámicos...",
  kioskMode = false,
  onPrintUser,
}) => {
  const [editingUser, setEditingUser] = useState<DynamicUser | null>(null)
  const [printingUser, setPrintingUser] = useState<DynamicUser | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [showHorizontalScroll, setShowHorizontalScroll] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const [preventClick, setPreventClick] = useState(false)
  const tableRef = useRef<HTMLDivElement>(null)
  const scrollbarRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const itemsPerPage = 10



  // Helper function to check if local user has modifications


  const filteredUsers = useMemo(() => {
    // En modo kiosco, solo mostrar resultados si hay búsqueda activa
    if (kioskMode && !searchTerm.trim()) {
      return []
    }
    return filterUsers(searchTerm)
  }, [filterUsers, searchTerm, kioskMode])

  // En modo kiosco, mostrar todos los campos visibles del schema (no filtrar por print)
  const visibleFieldsForKiosk = useMemo(() => {
    // En modo kiosco, mostrar todos los campos configurados como visibles
    // El atributo 'print' es solo para determinar qué se imprime, no qué se ve en tabla
    return config.visibleFields
  }, [config.visibleFields])

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage)
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredUsers, currentPage])

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  // Detectar si necesita scroll horizontal y manejar eventos
  useEffect(() => {
    const checkScrollNeed = () => {
      if (tableRef.current) {
        const needsHorizontalScroll = tableRef.current.scrollWidth > tableRef.current.clientWidth
        setShowHorizontalScroll(needsHorizontalScroll)
      }
    }

    const handleTableScroll = () => {
      if (tableRef.current && !isDragging) { // Solo actualizar si no estamos arrastrando
        setScrollLeft(tableRef.current.scrollLeft)
      }
    }

    // Observer para detectar cambios en el tamaño
    const resizeObserver = new ResizeObserver(checkScrollNeed)
    
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current)
      tableRef.current.addEventListener('scroll', handleTableScroll)
      checkScrollNeed()
    }

    return () => {
      if (tableRef.current) {
        tableRef.current.removeEventListener('scroll', handleTableScroll)
        resizeObserver.unobserve(tableRef.current)
      }
      resizeObserver.disconnect()
    }
  }, [paginatedUsers.length, filteredUsers.length])

  // Eventos de drag para el scrollbar
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !tableRef.current || !scrollbarRef.current) return

      const scrollbarRect = scrollbarRef.current.getBoundingClientRect()
      const scrollbarWidth = scrollbarRect.width
      
      // Calcular donde debería estar el thumb para mantener el mouse en la misma posición relativa
      const thumbLeft = e.clientX - dragOffset - scrollbarRect.left
      
      // Calcular el ancho del thumb
      const thumbWidthRatio = tableRef.current.clientWidth / tableRef.current.scrollWidth
      const thumbWidth = thumbWidthRatio * scrollbarWidth
      const maxThumbLeft = scrollbarWidth - thumbWidth
      
      // Limitar la posición del thumb dentro de los límites
      const clampedThumbLeft = Math.max(0, Math.min(maxThumbLeft, thumbLeft))
      
      // Calcular el scroll basado en la posición del thumb
      const percentage = maxThumbLeft > 0 ? clampedThumbLeft / maxThumbLeft : 0
      const maxScrollLeft = tableRef.current.scrollWidth - tableRef.current.clientWidth
      const newScrollLeft = percentage * maxScrollLeft
      
      tableRef.current.scrollLeft = newScrollLeft
    }

    const handleMouseUp = () => {
      if (isDragging) {
        setPreventClick(true)
        // Limpiar el preventClick después de un pequeño delay
        setTimeout(() => setPreventClick(false), 100)
      }
      setIsDragging(false)
      setDragOffset(0)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragOffset])

  // Función para manejar el click en la barra de scroll
  const handleScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!tableRef.current || !scrollbarRef.current || preventClick) return

    const scrollbarRect = scrollbarRef.current.getBoundingClientRect()
    const clickX = e.clientX - scrollbarRect.left
    const scrollbarWidth = scrollbarRect.width
    const maxScrollLeft = tableRef.current.scrollWidth - tableRef.current.clientWidth

    // Calcular nueva posición dentro de los límites
    const percentage = Math.max(0, Math.min(1, clickX / scrollbarWidth))
    const newScrollLeft = percentage * maxScrollLeft

    tableRef.current.scrollLeft = newScrollLeft
  }

  // Función para iniciar el drag
  const handleThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!scrollThumbRef.current || !scrollbarRef.current) return

    // Capturar el offset exacto donde se hizo click dentro del thumb
    const thumbRect = scrollThumbRef.current.getBoundingClientRect()
    const clickOffset = e.clientX - thumbRect.left
    
    setDragOffset(clickOffset) // Solo el offset dentro del thumb
    setIsDragging(true)
  }

  // Función para determinar el estado de validación de un usuario
  const getUserValidationStatus = (user: DynamicUser): 'complete' | 'incomplete' | 'invalid' => {
    // Obtener campos editables del schema
    const editableFields = SchemaService.getCreationFields(schema)
    
    let hasEmptyFields = false
    let hasInvalidEnums = false
    
    for (const { fieldName, fieldSchema } of editableFields) {
      // Usar SchemaService.getFieldValue directamente para obtener el valor crudo
      const rawValue = SchemaService.getFieldValue(user, fieldName)
      
      // Verificar si está realmente vacío (valor crudo)
      const isEmpty = rawValue === null || rawValue === undefined || 
                     (typeof rawValue === 'string' && rawValue.trim() === '') ||
                     (typeof rawValue === 'number' && isNaN(rawValue))
      
      // Verificar campos vacíos (CUALQUIER campo vacío, no solo required)
      if (isEmpty) {
        hasEmptyFields = true
      }
      
      // Verificar enums inválidos (solo si no está vacío)
      if (fieldSchema.type === 'enum' && !isEmpty) {
        const currentValue = String(rawValue).trim()
        const validOptions = fieldSchema.options || []
        const isValidOption = validOptions.includes(currentValue)
        
        if (!isValidOption) {
          hasInvalidEnums = true
          break // Prioridad a enums inválidos
        }
      }
    }
    
    // Prioridad: inválido > incompleto > completo
    if (hasInvalidEnums) return 'invalid'
    if (hasEmptyFields) return 'incomplete'
    return 'complete'
  }

  // Color utilities (REQ-002)
  const isValidHexColor = (color: string): boolean =>
    /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(color)

  const getTextColorForBg = (hex: string): string => {
    const clean = hex.replace("#", "")
    const full = clean.length === 3
      ? clean.split("").map(c => c + c).join("")
      : clean
    const r = parseInt(full.slice(0, 2), 16)
    const g = parseInt(full.slice(2, 4), 16)
    const b = parseInt(full.slice(4, 6), 16)
    return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? "#000000" : "#ffffff"
  }

  // Helper to render field value based on type
  const renderFieldValue = (user: DynamicUser, fieldName: string) => {
    const value = getFieldValue(user, fieldName)
    const fieldConfig = schema[fieldName]

    if (!fieldConfig) return value?.toString() || "-"

    // REQ-001: Badge color field rendering
    if (fieldName === "badge_color" || fieldName === "gafete_color") {
      const colorValue = String(value || "").trim()
      if (!colorValue) return <span className="text-amber-500">-</span>

      if (!isValidHexColor(colorValue)) {
        return (
          <div className="flex items-center gap-2">
            <span className="text-red-700 font-mono text-xs">{colorValue}</span>
            <span
              className="text-xs text-amber-600"
              title="Color hexadecimal no válido"
            >
              ⚠ Inválido
            </span>
          </div>
        )
      }

      const textColor = getTextColorForBg(colorValue)
      return (
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-6 h-6 rounded border-2 border-gray-400 shadow-md flex-shrink-0"
            style={{ backgroundColor: colorValue }}
            title={colorValue.toUpperCase()}
          />
          <span
            className="inline-flex px-2 py-0.5 text-xs font-mono font-semibold rounded-lg shadow-sm"
            style={{ backgroundColor: colorValue, color: textColor }}
          >
            {colorValue.toUpperCase()}
          </span>
        </div>
      )
    }

    // Special handling for boolean fields
    if (fieldConfig.type === 'boolean') {
      const boolValue = Boolean(value)
      return (
        <span
          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-xl backdrop-blur-sm border transition-all duration-300 ${
            boolValue 
              ? "bg-green-500/20 border-green-400/30 text-green-800 shadow-sm" 
              : "bg-gray-500/20 border-gray-400/30 text-gray-800 shadow-sm"
          }`}
        >
          {boolValue ? 'Sí' : 'No'}
        </span>
      )
    } else if (!value || String(value).trim() === '') {
      return (
        <span className=" text-amber-500 shadow-sm">
          -
        </span>
      )
    } else if (fieldConfig.type === 'enum') {
      const currentValue = String(value || '').trim()
      const validOptions = fieldConfig.options || []
      const isValidOption = validOptions.includes(currentValue)
      
      if (isValidOption) {
        // Valor válido del enum
        return (
          <span>
            {value}
          </span>
        )
      } else {
        // Valor no válido del enum - mostrar advertencia
        return (
          <div className="flex items-center space-x-2">
            <span className=" text-red-700">
              {value}
            </span>
            <div className="group relative">
              <svg className="h-4 w-4 text-amber-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {/* Tooltip de advertencia */}
              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
                <div className="bg-amber-600/90 backdrop-blur-sm text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg border border-amber-500/20">
                  <div className="font-medium">Valor no válido</div>
                  <div className="text-amber-100">"{currentValue}" no coincide con las opciones predeterminadas</div>
                  {/* Flecha del tooltip */}
                  <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-amber-600/90"></div>
                </div>
              </div>
            </div>
          </div>
        )
      }
    }

    return formatFieldValue(value, fieldName)
  }

  // Get the fixed field (left column)
  const fixedField = config.fixedField

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <div className="text-red-600 mb-2">Error al cargar los datos dinámicos</div>
          <div className="text-gray-500 text-sm">{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="pb-20">
      <div className="bg-white/60 backdrop-blur-md border border-gray-200/50 rounded-xl shadow-lg overflow-hidden">
        {/* Header with export button */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">
            {kioskMode ? "Resultados de Búsqueda" : "Lista de Usuarios"}
          </h2>
          {!kioskMode && (
            <button
              id="export-btn"
              onClick={() => exportToExcel(filteredUsers, schema, config, getFieldValue, formatFieldValue)}
              className="inline-flex items-center px-3 py-1.5 bg-emerald-600/20 backdrop-blur-sm border border-emerald-400/30 text-emerald-800 text-sm font-medium rounded-xl hover:bg-emerald-600/30 hover:border-emerald-500/40 hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-300"
              title="Exportar a Excel"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Exportar Excel
            </button>
          )}
        </div>

        {/* Table */}
        <div ref={tableRef} className="overflow-x-auto scrollbar-hide">
          <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              {/* Fixed left column */}
              {fixedField && (
                <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                  {schema[fixedField]?.label || fixedField.toUpperCase()}
                </th>
              )}
              
              {/* Dynamic columns */}
              {(kioskMode ? visibleFieldsForKiosk : config.visibleFields)
                .filter(field => field !== fixedField) // Don't duplicate fixed field
                .map((fieldName) => (
                  <th 
                    key={fieldName}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {schema[fieldName]?.label || fieldName.toUpperCase()}
                  </th>
                ))}
              
              {/* Fixed right column - Actions */}
              <th className="sticky right-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                ACCIONES
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={config.visibleFields.length + 1} className="px-6 py-12 text-center text-gray-500">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      <span className="text-sm font-medium">Cargando usuarios dinámicos...</span>
                    </div>
                    <div className="text-xs text-gray-400">{loadingStep}</div>
                  </div>
                </td>
              </tr>
            ) : paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={config.visibleFields.length + 1} className="px-6 py-12 text-center text-gray-500">
                  No se encontraron usuarios
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user, index) => {
                
                return (
                  <tr
                    key={`${user.id}-${currentPage}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* Fixed left column */}
                    {fixedField && (() => {
                      const validationStatus = getUserValidationStatus(user)
                      const stripColor =
                        validationStatus === 'invalid'
                          ? 'bg-red-500'
                          : validationStatus === 'incomplete'
                            ? 'bg-amber-400'
                            : 'bg-transparent'
                      const stripTitle =
                        validationStatus === 'invalid'
                          ? 'Registro con valores inválidos — abrir Editar'
                          : validationStatus === 'incomplete'
                            ? 'Faltan campos por llenar — abrir Editar'
                            : ''
                      return (
                        <td
                          id={index === 0 ? "fixed-column" : undefined}
                          className="sticky left-0 z-20 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                        >
                          {/* Barra de estado de completitud a la izquierda (nivel fila) */}
                          <span
                            className={`absolute inset-y-0 left-0 w-1.5 ${stripColor}`}
                            title={stripTitle}
                            aria-label={stripTitle || undefined}
                          />
                          <span className="block pr-1">{renderFieldValue(user, fixedField)}</span>
                          <span
                            className="absolute inset-y-0 right-0 w-px bg-gray-200"
                          />
                        </td>
                      )
                    })()}
                    
                    {/* Dynamic columns */}
                    {(kioskMode ? visibleFieldsForKiosk : config.visibleFields)
                      .filter(field => field !== fixedField)
                      .map((fieldName) => (
                        <td 
                          key={fieldName}
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                        >
                          {renderFieldValue(user, fieldName)}
                        </td>
                      ))}
                    
                    {/* Fixed right column - Actions */}
                    <td className="sticky right-0 z-10 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium border-l border-gray-200 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                      <div className="flex space-x-2">
                        {!kioskMode && (
                          <button
                            id={index === 0 ? "edit-btn" : undefined}
                            onClick={() => setEditingUser(user)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                            aria-label={`Editar usuario ${user.id}`}
                            title={
                              getUserValidationStatus(user) === 'invalid'
                                ? 'Editar — hay valores inválidos'
                                : getUserValidationStatus(user) === 'incomplete'
                                  ? 'Editar — faltan campos por llenar'
                                  : 'Editar usuario'
                            }
                          >
                            <Edit className="h-4 w-4" aria-hidden="true" />
                          </button>
                        )}
                        <div className="relative">
                          <button
                            id={index === 0 ? "print-btn" : undefined}
                            onClick={() => kioskMode ? onPrintUser?.(user) : setPrintingUser(user)}
                            className={`text-green-600 hover:text-green-900 rounded hover:bg-green-50 transition-colors ${
                              kioskMode ? 'inline-flex items-center px-3 py-2 text-sm font-medium' : 'p-1'
                            }`}
                            aria-label={`Imprimir gafete de usuario ${user.id}`}
                            title={!kioskMode && getUserValidationStatus(user) !== 'complete' ? "Faltan campos por llenar — ver módulo Editar" : "Imprimir gafete"}
                          >
                            <Printer className="h-4 w-4" aria-hidden="true" />
                            {kioskMode && <span className="ml-2">Imprimir Gafete</span>}
                          </button>
                          {!kioskMode && getUserValidationStatus(user) !== 'complete' && (
                            <span className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-amber-400 pointer-events-none" aria-hidden="true" />
                          )}
                          <div
                            className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3 h-0.5 rounded-full ${
                              Boolean(getFieldValue(user, 'impreso')) ? "bg-green-400" : "bg-gray-400 opacity-20"
                            }`}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          id="pagination"
          className="fixed bottom-0 left-1/2 transform -translate-x-1/2 z-20 bg-white/60 backdrop-blur-md border border-gray-200/50 rounded-xl px-4 py-3 sm:px-6 shadow-[0_-4px_16px_rgba(0,0,0,0.1)] max-w-7xl mx-auto"
          style={{ width: 'calc(100vw - 3rem)' }}
        >
          {/* Horizontal Scrollbar (solo si es necesario) */}
          {showHorizontalScroll && tableRef.current && (
            <div className="mb-3 px-2">
              <div className="text-xs text-gray-500 mb-1">Deslizar tabla horizontalmente:</div>
              <div
                ref={scrollbarRef}
                className="relative h-4 bg-gray-200/50 rounded-full cursor-pointer"
                onClick={handleScrollbarClick}
              >
                <div
                  ref={scrollThumbRef}
                  data-scrollbar-thumb
                  className="absolute top-0 h-4 bg-blue-400/60 rounded-full cursor-grab active:cursor-grabbing"
                  style={{
                    left: `${(() => {
                      const thumbWidthPercentage = (tableRef.current.clientWidth / tableRef.current.scrollWidth) * 100
                      const maxScrollableArea = 100 - thumbWidthPercentage
                      const scrollProgress = scrollLeft / (tableRef.current.scrollWidth - tableRef.current.clientWidth)
                      return Math.max(0, Math.min(maxScrollableArea, scrollProgress * maxScrollableArea))
                    })()}%`,
                    width: `${(tableRef.current.clientWidth / tableRef.current.scrollWidth) * 100}%`,
                  }}
                  onMouseDown={handleThumbMouseDown}
                />
              </div>
            </div>
          )}

          {/* Paginación normal */}
          <div className="flex items-center justify-between">
          {/* Mobile pagination */}
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center px-4 py-2 bg-gray-100/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-100/30 hover:border-gray-400/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="inline-flex items-center px-4 py-2 bg-gray-100/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-100/30 hover:border-gray-400/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>

          {/* Desktop pagination */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a{" "}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredUsers.length)}</span>{" "}
                de <span className="font-medium">{filteredUsers.length}</span> resultados
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {/* Primera página */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 bg-gray-100/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-100/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Primera página"
              >
                <ChevronsLeft className="h-4 w-4" aria-hidden="true" />
              </button>

              {/* Página anterior */}
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 bg-gray-100/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-100/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>

              {/* Números de página */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let page: number
                  if (totalPages <= 7) {
                    page = i + 1
                  } else if (currentPage <= 4) {
                    page = i + 1
                  } else if (currentPage >= totalPages - 3) {
                    page = totalPages - 6 + i
                  } else {
                    page = currentPage - 3 + i
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl backdrop-blur-sm border transition-all duration-300 ${
                        page === currentPage
                          ? "bg-blue-500/20 border-blue-400/30 text-blue-800 shadow-lg shadow-blue-500/20"
                          : "bg-gray-100/20 border-gray-300/30 text-gray-800 hover:bg-gray-100/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20"
                      }`}
                    >
                      {page}
                    </button>
                  )
                })}
              </div>

              {/* Página siguiente */}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 bg-gray-100/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-100/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Página siguiente"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>

              {/* Última página */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 bg-gray-100/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-100/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Última página"
              >
                <ChevronsRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Dynamic Print Modal - Solo en modo normal (no kiosco) */}
      {printingUser && !kioskMode && (
        <DynamicPrintModal
          user={printingUser}
          schema={schema}
          onClose={() => setPrintingUser(null)}
          onPrintSuccess={(updatedUser) => {
            onEditSuccess(updatedUser)
            setPrintingUser(null)
          }}
          showToast={showToast}
          cancelCurrentFetch={cancelCurrentFetch}
        />
      )}

      {/* Dynamic Edit Modal */}
      {editingUser && (
        <DynamicEditModal
          user={editingUser}
          schema={schema}
          onClose={() => setEditingUser(null)}
          onSuccess={(updatedUser) => {
            onEditSuccess(updatedUser)
            setEditingUser(null)
          }}
          showToast={showToast}
          cancelCurrentFetch={cancelCurrentFetch}
        />
      )}
    </div>
  )
}

export default DynamicTable
